import { Box, Typography, Tooltip, useMediaQuery } from '@mui/material';
import { Theme } from '@mui/material/styles';
import { Icon, IconButton } from '@sm-dashboard/ui-components';

interface PageNavigatorProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}

export const PageNavigator = ({ currentPage, totalPages, onPageChange }: PageNavigatorProps) => {
  const isSmallScreen = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
  const renderPageNumbers = () => {
    const pageTypography = (page: number, isCurrent = false) => (
      <Typography
        key={page}
        onClick={() => !isCurrent && onPageChange(page - 1)}
        variant="body2"
        sx={{
          cursor: isCurrent ? 'default' : 'pointer',
          fontWeight: isCurrent ? 'bold' : 'normal',
          '&:hover': { textDecoration: 'underline' }
        }}
      >
        {page}
      </Typography>
    );
    const pages = [];
    const currentPageNum = currentPage + 1;

    // Calculate the range of pages to show
    const pagesBefore = isSmallScreen ? 1 : 3;
    const pagesAfter = isSmallScreen ? 1 : 3;
    const dots = isSmallScreen ? '.' : '. .';
    const gap = 1;
    const isGapBefore = currentPageNum - gap - pagesBefore > 0;
    const isGapAfter = totalPages + 1 - currentPageNum - gap - pagesAfter > 0;

    const startPage = Math.max(1, currentPageNum - pagesBefore - gap);
    const endPage = Math.min(totalPages, currentPageNum + pagesAfter + gap);

    // Add pages before current page
    for (let i = startPage; i < currentPageNum - (isGapBefore ? gap : 0); i++) {
      pages.push(pageTypography(i));
    }

    // Add dots before current page if there are more pages before
    if (isGapBefore) {
      pages.push(
        <Typography key="dots1" variant="body2">
          {dots}
        </Typography>
      );
    }

    // Add current page
    pages.push(pageTypography(currentPageNum, true));

    // Add dots after current page if there are more pages after
    if (isGapAfter) {
      pages.push(
        <Typography key="dots2" variant="body2">
          {dots}
        </Typography>
      );
    }

    // Add pages after current page
    for (let i = currentPageNum + (isGapAfter ? gap : 0) + 1; i <= endPage; i++) {
      pages.push(pageTypography(i));
    }

    return pages.reverse();
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Tooltip title="הבא">
        <Box component="span">
          <IconButton
            variant="text"
            color="secondary"
            shape="circle"
            size="small"
            aria-label="הבא"
            onClick={() => onPageChange(Math.min(totalPages - 1, currentPage + 1))}
            disabled={currentPage === totalPages - 1}
          >
            <Icon icon="chevronRight" color="inherit" size="small" />
          </IconButton>
        </Box>
      </Tooltip>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>{renderPageNumbers()}</Box>

      <Tooltip title="הקודם">
        <Box component="span">
          <IconButton
            variant="text"
            color="secondary"
            shape="circle"
            size="small"
            aria-label="הקודם"
            onClick={() => onPageChange(Math.max(0, currentPage - 1))}
            disabled={currentPage === 0}
          >
            <Icon icon="chevronLeft" color="inherit" size="small" />
          </IconButton>
        </Box>
      </Tooltip>
    </Box>
  );
};
